import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref } from "vue"

const _hoisted_1 = { class: "toplist" }
const _hoisted_2 = ["onMouseenter", "onClick"]
const _hoisted_3 = { class: "item-label" }
const _hoisted_4 = { class: "item-chart" }

import { computed } from 'vue'
import { Limits } from './ChartComponent/chart/limits'
import { FormatOptions } from '../formatter'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { Bar, createBars } from './ChartComponent/chart/bars'
import { AreaHoverHighlight, createAreaHightlights } from './ChartComponent/chart/area-highlight'
import { createTooltip } from './ChartComponent/chart/tooltip'
import { ChartComponent } from './ChartComponent'
import ChartBase from './ChartComponent/ChartBase.vue'
import { useI18n } from 'vue-i18n'
import { useHoverStore } from '@/pinia/hover'
import { useEntityLookup } from '@/composables/useEntityLookup'
import { MeasurementPoint } from '../measurements/contracts'
import useNavigateTo from '@/composables/useNavigateTo'
import { BarConfiguration } from './toplist-types'

type Props = {
  data: MeasurementPoint[]
  format: FormatOptions
  locale: string
  barOptions: BarConfiguration[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TopListNew',
  props: {
    data: {},
    format: {},
    locale: {},
    barOptions: {}
  },
  setup(__props: any) {

const props = __props

const { navigateTo } = useNavigateTo()
const { addHoveredItem, removeLastHoveredItem } = useHoverStore()
const { findEntity, getEntityDisplayString } = useEntityLookup()

function onMouseEnter(id: string) {
  const entity = findEntity(id)
  if (entity) {
    addHoveredItem(entity.id, { color: '#fadb14' })
  }
}

async function onMouseClick(id: string) {
  await navigateTo(id)
}

function getEntityName(id: string) {
  return getEntityDisplayString(id, true) ?? id
}

const limits: Limits = {
  x: [0, 1],
  y: [0, 1]
}

const max = computed(() => {
  if (!props.data || props.data.length === 0) return 0
  return props.data
    .map((m) => (Object.values(m.fields) as number[]).reduce((a, b) => a + b, 0))
    .reduce((a, b) => Math.max(a, b), -Infinity)
})

const rows = computed(() => {
  if (!props.data || props.data.length === 0) return []

  const globalMaxValue = max.value

  return props.data.map((m) => {
    const id = m.path?.split('.')?.slice(-1)[0] ?? '-'
    const fieldValues = Object.values(m.fields) as number[]
    const sumForRow = fieldValues.reduce((a, b) => a + b, 0)

    let proportionFilled: number
    if (globalMaxValue <= 0) {
      proportionFilled = 0
    } else {
      proportionFilled = sumForRow / globalMaxValue
    }

    proportionFilled = Math.max(0, Math.min(1, proportionFilled))

    const proportionRemainder = 1 - proportionFilled

    return {
      id: id,

      normalizedProportions: [proportionFilled, proportionRemainder],

      originalSegmentValues: [sumForRow, Math.max(0, globalMaxValue - sumForRow)],
      originalTotalForRow: sumForRow
    }
  })
})

const rowComponents = computed<ChartComponent[][]>(() => {
  const locale = useI18n().locale.value

  const barsForEachRow: Bar[][] = rows.value.map((row) => {
    return row.normalizedProportions.map((segmentProportion, segmentIndex) => {
      const barConfig = props.barOptions[segmentIndex]

      if (!barConfig) {
        console.warn(
          `Toplist: Missing bar configuration for segment index ${segmentIndex}. Ensure props.barOptions has at least two entries.`
        )

        return { x: 0.5, height: segmentProportion, color: 'transparent' } as Bar
      }

      const bar: Bar = {
        ...barConfig,
        x: 0.5,
        height: segmentProportion,
        label: barConfig.label?.[locale]
      }
      return bar
    })
  })

  const makeHighlights = (proportions: number[], originalSegmentValues: number[]) => {
    let temp: AreaHoverHighlight[] = []
    let accumulatedProportion = 0

    for (let i = 0; i < proportions.length; i++) {
      const segmentProportion = proportions[i]
      const barOptionForSegment = props.barOptions[i]

      if (!barOptionForSegment) continue

      const currentSegmentStartX = accumulatedProportion
      const currentSegmentEndX = accumulatedProportion + segmentProportion

      if (barOptionForSegment.tooltip !== false && segmentProportion > 1e-6) {
        const actualValueForSegment = originalSegmentValues[i]

        const highlight: AreaHoverHighlight = {
          label: barOptionForSegment.label?.[locale],
          color: barOptionForSegment.color,
          pattern: barOptionForSegment.pattern,
          path: [
            [currentSegmentStartX, 0],
            [currentSegmentEndX, 0],
            [currentSegmentEndX, 1],
            [currentSegmentStartX, 1]
          ],
          value: [0, actualValueForSegment],
          visible: false
        }
        temp.push(highlight)
      }
      accumulatedProportion = currentSegmentEndX
    }
    return temp
  }

  const finalBarComponents = barsForEachRow.map((singleRowSegmentBars, rowIndex) => {
    const currentRowData = rows.value[rowIndex]
    return [
      ...createTooltip(limits, props.format),
      ...createBars([singleRowSegmentBars], 1, 'horizontal', limits),
      ...createAreaHightlights(
        makeHighlights(currentRowData.normalizedProportions, currentRowData.originalSegmentValues),
        limits
      )
    ]
  })
  return finalBarComponents
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row, index) => {
      return (_openBlock(), _createElementBlock("div", {
        key: row.id,
        class: "toplist-item",
        onMouseenter: () => onMouseEnter(row.id),
        onMouseleave: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(removeLastHoveredItem) && _unref(removeLastHoveredItem)(...args))),
        onClick: () => onMouseClick(row.id)
      }, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(TextBase, {
            text: getEntityName(row.id),
            weight: 'bold',
            type: 'label',
            style: { 'line-height': 'unset' }
          }, null, 8, ["text"]),
          _createVNode(TextBase, {
            text: '',
            weight: 'bold',
            type: 'label',
            color: '#767676',
            style: { 'line-height': 'unset' }
          })
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(ChartBase, {
            height: "16px",
            style: {
            width: '100%',
            height: '16px',
            color: '#282828'
          },
            paddingOverride: { top: 20, bottom: 20, left: 0, right: 0 },
            components: rowComponents.value[index]
          }, null, 8, ["components"])
        ])
      ], 40, _hoisted_2))
    }), 128))
  ]))
}
}

})